import { AxiosPromise } from 'axios';
import { SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { DATE_FORMAT_WITH_TIMEZONE } from '~/constants/date';
import format from 'date-fns/format';
import * as moment from 'moment';
import api, { apiV1, armorAPI, djangoApi } from '~/services/api';
import Address, {
  AddressReturnType,
  AddressToType,
  AddressType,
  BaseAddress,
  toJSON,
} from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import { JSONOrder, newOrder, SavedJSONOrder } from '~/types/Order';
import {
  extrasToJSON,
  JSONBatchEntriesResponse,
  JSONBatchEntryExtended,
  JSONPartialFulfillmentContents,
  ShipmentExtras,
} from '~/types/OrderBatchEntry';
import { BatchEntryId, JSONOrdersBatch, JSONUserBatchInput } from '~/types/OrdersBatch';
import Parcel, { SavedJSONParcel, toJSON as parcelToJSON } from '~/types/Parcel';
import { JSONTransactionUI2 } from '~/types/Transaction';

const X_ORDERS_SEARCH_VERSION = 'ORDERS_SEARCH_ORDER_NUMBER';

export function getLegacy(orderId: string) {
  return api.request({
    url: `/orders/${orderId}`,
    method: 'GET',
    headers: { Legacy: true },
  });
}

// TODO: change to latest version once we fix the count issue
export function getAllLegacy(
  page: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  fetchCount: string,
  showOnlyHidden: boolean,
  sort: string,
) {
  return apiV1.request({
    url: '/orders',
    method: 'GET',
    headers: { Legacy: true },
    params: {
      ...filters,
      page,
      sort: sort !== '' ? sort : undefined,
      ...(fetchCount !== '' ? { results: fetchCount } : {}),
      hidden: showOnlyHidden,
    },
  });
}

export function getAll(
  page: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  fetchCount: string,
  showOnlyHidden: boolean,
  sort: string,
  sortBy: string,
  orderBy: string,
  object_id: string[] = [],
) {
  return armorAPI.request<{
    results: JSONOrder[];
    count: number;
  }>({
    url: '/orders',
    method: 'GET',
    headers: { X_ORDERS_SEARCH_VERSION },
    params: {
      ...filters,
      page,
      sort: sort !== '' ? sort : undefined,
      ...(sortBy?.length ? { sort_by: sortBy } : {}),
      ...(orderBy?.length ? { order_by: orderBy } : {}),
      ...(fetchCount?.length ? { results: fetchCount } : {}),
      hidden: showOnlyHidden,
      ...(filters.is_international
        ? { destination: filters.is_international === 'true' ? 'international' : 'domestic' }
        : {}),
      ...(object_id?.length ? { object_id } : {}),
    },
  });
}

export function get(orderId: string) {
  return armorAPI.request<JSONOrder>({
    url: `/orders/${orderId}`,
    method: 'GET',
  });
}

export function getOrderTransactions(orderObjectId: string) {
  return apiV1.request<{ results: JSONTransactionUI2[] }>({
    url: `/orders/${orderObjectId}/transactions`,
    method: 'GET',
    params: {
      results: 200,
    },
  });
}

export function createOrder(order: ReturnType<typeof newOrder>) {
  return apiV1.request<JSONOrder>({
    url: 'orders/',
    method: 'POST',
    data: order,
  });
}

// TODO: change to latest version once we fix the count issue
// Just get one order to get the count
export function countOrdersLegacy() {
  return apiV1.request<{ results: JSONOrder[] }>({
    url: '/orders',
    method: 'GET',
    headers: { Legacy: true },
    params: {
      results: 1,
      page: 1,
    },
  });
}

export function countOrders() {
  return armorAPI.request<{ results: JSONOrder[] }>({
    url: '/orders',
    method: 'GET',
    params: {
      results: 1,
      page: 1,
    },
  });
}

export function getBatch(batchId: number) {
  return armorAPI.request<JSONOrdersBatch>({
    url: `/batch/${batchId}`,
    method: 'GET',
  });
}

export function createBatch(orderObjectIds: string[], batchEntryIds: number[]) {
  return armorAPI.request<JSONOrdersBatch>({
    url: `/batch/`,
    method: 'POST',
    data: {
      orders: orderObjectIds,
      batch_entries: batchEntryIds,
    },
  });
}

export function regenerateBatchEntry(
  batchId: number,
  batchEntryId: number,
  packageInformation: JSONUserBatchInput,
) {
  return armorAPI.request({
    url: `/batch/${batchId}/entries/${batchEntryId}/regenerate`,
    method: 'POST',
    data: packageInformation,
  });
}

export function saveBatchPackageInformation(
  batchId: number,
  packageInformation: JSONUserBatchInput,
) {
  return armorAPI.request({
    url: `/batch/${batchId}/shipments`,
    method: 'POST',
    data: packageInformation,
  });
}

export function createBatchTransactions(
  batchId: number,
  rates: string[],
  excludedOrders: string[],
) {
  return armorAPI.request<{
    batch_entries: { id: number }[];
    // TODO: Verify this type!
    transactions: { object_id: string; batch_entry_id: number }[];
    remaining_labels_until_limit: number;
    label_limit_renewal_date: string;
  }>({
    url: `/batch/${batchId}/transactions`,
    method: 'POST',
    data: { rates, excluded_orders: excludedOrders },
  });
}

export function downloadPackingSlips(batchId: number) {
  return api.request<{ slip_url: string }>({
    url: 'orders/packingslip',
    method: 'GET',
    params: {
      batch_id: batchId,
    },
  });
}

export function downloadAllLabels(batchId: number) {
  return djangoApi.request({
    url: `user/orders/download/batch/v2/${batchId}/`,
    method: 'POST',
  });
}

export function syncOrders() {
  return api.request({
    url: 'orders/sync',
    method: 'GET',
  });
}

// export functions for all orders page
export function getOrderPackingSlip(orderObjectIds: string[], batchEntryIds: number[]) {
  return api.request<{ slip_url: string }>({
    url: `orders/packingslip`,
    method: 'GET',
    params: {
      id: orderObjectIds,
      be_id: batchEntryIds,
    },
  });
}

export function getAllLabels(orderObjectIds: string[], batchEntryIds: BatchEntryId[]) {
  // TODO: This response has more than just the URL, please improve the interface
  return apiV1.request<{ url: string }>({
    url: 'orders/',
    method: 'POST',
    data: {
      batch_entries: batchEntryIds,
      action: {
        list: orderObjectIds,
        type: 'merge_labels',
      },
    },
  });
}

export function getLabel(transactionIds: string[], fileFormat: string) {
  return armorAPI.request({
    url: 'label_regen/',
    method: 'POST',
    data: {
      transactions: transactionIds,
      legacy_file_format: fileFormat,
    },
  });
}

export function hideOrder(orderObjectIds: string[]) {
  return apiV1.request({
    url: 'orders/',
    method: 'POST',
    data: {
      action: {
        list: orderObjectIds,
        type: 'hide',
      },
    },
  });
}

export function createBatchEntriesForOrders(orderObjectIds: string[], isRating?: boolean) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: 'orders/batch_entry',
    method: 'POST',
    data: {
      orders: orderObjectIds,
      is_rating: !!isRating,
    },
  });
}

export function getOrCloneBatchEntriesForOrders(orderObjectIDs: string[], batchEntryIDs: number[]) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: 'orders/batch_entry',
    method: 'POST',
    data: {
      orders: orderObjectIDs,
      batch_entries: batchEntryIDs,
      is_integrated: true,
    },
  });
}

const buildRequestParcel = (parcel: SavedJSONParcel) => {
  const parcelData = parcel.use_imported_order_weight
    ? {
        use_imported_order_weight: parcel.use_imported_order_weight,
      }
    : {
        weight: parcel.weight,
        mass_unit: parcel.mass_unit,
      };

  const carrierTemplateVariableDimensions =
    parcel.carrier_parcel_template && parcel.carrier_parcel_template.is_variable_dimensions;

  const requestParcel = parcel.user_parcel_template
    ? {
        ...parcelData,
        user_parcel_template_object_id:
          parcel.user_parcel_template && parcel.user_parcel_template.object_id,
      } // user parcel template
    : parcel.carrier_parcel_template && !carrierTemplateVariableDimensions
      ? {
          ...parcelData,
          carrier_parcel_template_token:
            parcel.carrier_parcel_template && parcel.carrier_parcel_template.token,
        } // carrier parcel template, not variable dimension
      : parcel.carrier_parcel_template && carrierTemplateVariableDimensions
        ? {
            ...parcelData,
            carrier_parcel_template_token:
              parcel.carrier_parcel_template && parcel.carrier_parcel_template.token,
            width: parcel.width,
            length: parcel.length,
            height: parcel.height,
            distance_unit: parcel.distance_unit,
          } // carrier parcel template, variable dimension (needs dimensions in payload)
        : {
            ...parcelData,
            width: parcel.width,
            length: parcel.length,
            height: parcel.height,
            distance_unit: parcel.distance_unit,
          }; // custom dimensions

  return requestParcel;
};

export function patchBatchEntries(
  batchEntryIds: number[],
  parcel: Maybe<Parcel>,
  extras: Maybe<Partial<ShipmentExtras>>,
): AxiosPromise<JSONBatchEntryExtended[]> {
  const requestParcel = parcel ? buildRequestParcel(parcelToJSON(parcel)) : undefined;
  const requestExtras = extras ? extrasToJSON(extras) : undefined;

  return armorAPI.request({
    url: 'batch_entry/',
    method: 'PATCH',
    data: {
      batch_entries: batchEntryIds,
      parcel: requestParcel,
      extras: requestExtras,
    },
  });
}

export function updateParcel(batchEntryIds: number[], parcel: SavedJSONParcel) {
  const requestParcel = buildRequestParcel(parcel);

  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: 'orders/batch_entry/parcel',
    method: 'PUT',
    data: {
      parcel: requestParcel,
      batch_entries: batchEntryIds,
    },
  });
}

export function updateBatchEntryExtras(
  batchEntryIds: number[],
  extras: ShipmentExtras,
): AxiosPromise<JSONBatchEntryExtended[]> {
  return armorAPI.request({
    url: 'orders/batch_entry/extras',
    method: 'PUT',
    data: {
      batch_entries: batchEntryIds,
      extras: extrasToJSON(extras),
    },
  });
}

export function patchUpdateBatchEntryExtras(
  batchEntryIds: number[],
  extras: Partial<ShipmentExtras>,
) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: 'orders/batch_entry/extras',
    method: 'PATCH',
    data: {
      batch_entries: batchEntryIds,
      extras: extrasToJSON(extras),
    },
  });
}

export function updateBatchEntryAddress(
  address: number | BaseAddress,
  addressType: AddressType,
  batchEntryIds: number[],
) {
  const parsedAddress = (address as APIAddress).id
    ? (address as APIAddress).id
    : toJSON(address as Address);
  const convertedAddressType: AddressToType | AddressReturnType | 'from' =
    addressType === SENDER_ADDRESS_KIND ? 'from' : addressType; // Conversion needed since BE uses 'from' but FE uses 'sender'
  const url = `orders/batch_entry/address_${convertedAddressType}`;
  const addressKey =
    typeof parsedAddress === 'number'
      ? `address_${convertedAddressType}_id`
      : `address_${convertedAddressType}`;

  const data = {
    batch_entries: batchEntryIds,
    [addressKey]: parsedAddress,
  };

  return armorAPI.request<JSONBatchEntryExtended[]>({
    url,
    data,
    method: 'PUT',
  });
}

export function updateBatchEntryServiceLevelToken(
  preferredServiceLevelToken: string,
  batchEntryIds: number[],
  accountObjectId?: string,
) {
  return armorAPI.request<JSONBatchEntriesResponse>({
    data: {
      batch_entries: batchEntryIds,
      preferred_service_level_token: preferredServiceLevelToken,
      preferred_account_object_id: accountObjectId,
    },
    method: 'PUT',
    url: `batch_entry/service_level`,
  });
}

export function updateBatchEntryShipmentDate(
  shipmentDate: moment.Moment | number,
  batchEntryIds: number[],
) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: '/batch_entry/shipment_date',
    data: {
      batch_entries: batchEntryIds,
      shipment_date: moment.isMoment(shipmentDate)
        ? shipmentDate.format() // preserves original timezone instead of converting to UTC
        : format(shipmentDate, DATE_FORMAT_WITH_TIMEZONE),
    },
    method: 'PUT',
  });
}

export function refreshBatchEntry(batchEntryIds: number[]) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: '/orders/batch_entry/refresh',
    method: 'PUT',
    data: {
      batch_entries: batchEntryIds,
    },
  });
}

export function updateOrder(order: Partial<SavedJSONOrder>) {
  return apiV1.request<JSONOrder>({
    url: `orders/${order.object_id}`,
    method: 'PUT',
    data: order,
  });
}

// TODO: create order-batch.services
export function getUserBatchInput(batchId: string | number, batchEntryId: string | number) {
  return armorAPI.request<JSONUserBatchInput>({
    url: `/batch/${batchId}/entries/${batchEntryId}/input`,
    method: 'GET',
  });
}

export function getOrderBatchEntry(orderObjectId: string | number) {
  return armorAPI.request<JSONBatchEntryExtended[]>({
    url: `/orders/${orderObjectId}/batch_entry`,
    method: 'GET',
  });
}

export function getOrderBatchEntries(orderObjectIds: string[]) {
  return armorAPI.request<{ results: JSONBatchEntryExtended[] }>({
    url: '/batch_entry',
    method: 'GET',
    params: {
      orders: orderObjectIds,
    },
  });
}

// grabs all rules
export function fetchBatchEntryRules(batchEntryId: number) {
  return armorAPI.request({
    url: `/batch_entry/${batchEntryId}/rule`,
    method: 'GET',
  });
}

// Partial fulfillment / order split services
export function createPartialFulfillmentRequests(
  orderObjectId: string | number,
  partialFulfillmentRequests: JSONPartialFulfillmentContents[],
) {
  return armorAPI.request<JSONOrder[]>({
    url: `orders/${orderObjectId}/split`,
    method: 'POST',
    data: {
      shipments: partialFulfillmentRequests,
    },
  });
}
